import { useEffect } from 'react';
import { useAppDispatch } from './useRedux';
import { setScreenShareAspectAction } from 'flow/actions/video-actions';

/**
 * Reports the true aspect ratio (w/h) of an on-stage shared screen to the store
 * so the layout engine can size the stage to it instead of assuming landscape.
 *
 * Attach to the screen-share `<video>` element and pass `active` true only while
 * that video is the stage share. The intrinsic `videoWidth/videoHeight` are read
 * on metadata load and whenever the source resolution changes; the aspect is
 * cleared on cleanup, so it resets the moment the share leaves the stage.
 *
 * Pass `sourceKey` (the stream/peer driving the video) when the `<video>` mounts
 * lazily — e.g. an element rendered only once its stream exists. The effect
 * re-attaches when it changes, so a video that mounts after `active` is already
 * true (a stream arriving a tick late) still gets measured.
 */
const useReportScreenShareAspect = (
    videoRef: React.RefObject<HTMLVideoElement>,
    active: boolean,
    sourceKey?: unknown,
): void => {
    const dispatch = useAppDispatch();

    useEffect(() => {
        const video = videoRef.current;
        if (!active || !video) {
            return;
        }
        const report = () => {
            const { videoWidth, videoHeight } = video;
            if (videoWidth > 0 && videoHeight > 0) {
                dispatch(setScreenShareAspectAction(videoWidth / videoHeight));
            }
        };
        report();
        video.addEventListener('loadedmetadata', report);
        video.addEventListener('resize', report);
        return () => {
            video.removeEventListener('loadedmetadata', report);
            video.removeEventListener('resize', report);
            dispatch(setScreenShareAspectAction(null));
        };
    }, [videoRef, active, sourceKey, dispatch]);
};

export default useReportScreenShareAspect;
